/**
 * Serviço para gerenciamento de tokens de autenticação
 * REFATORADO: Agora usa TokenManager como backend unificado
 * Mantém interface existente para compatibilidade
 */

import { TokenManager } from './auth/tokenManager';
import { logger } from './terminalLogger';

// Re-export TokenManager para uso direto (opcional)
export { TokenManager };

/**
 * Interface legada mantida para compatibilidade com código existente
 * Todas as funções delegam para TokenManager
 */
export const TokenService = {
  /**
   * Armazena tokens após login/refresh
   */
  setTokens(accessToken: string, refreshToken?: string, userData?: any): void {
    TokenManager.setTokens(accessToken, refreshToken, userData);
  },

  /** Retorna accessToken atual */
  getAccessToken(): string | null {
    return TokenManager.getAccessToken();
  },

  /** Retorna refreshToken (memória; fallback opcional: sessionStorage) */
  getRefreshToken(): string | null {
    return TokenManager.getRefreshToken();
  },

  /** Obtém dados do usuário armazenados na sessão */
  getUserData(): any | null {
    const userData = TokenManager.getUserData();
    
    if (!userData) {
      logger.debug('[TOKEN_SERVICE] No user data in storage');
      return null;
    }
    
    // Logs para debug (mantido da versão original)
    logger.debug('[TOKEN_SERVICE] Retrieved user data from storage', {
      hasData: !!userData?.data,
      permissions: userData?.data?.permissions,
      permissionsConfig: userData?.data?.permissionsConfig?.map((c: any) => ({
        perm_id: c.perm_id,
        role_name: c.role_name
      })),
      permissionsConfigLength: userData?.data?.permissionsConfig?.length || 0
    });
    
    return userData;
  },

  /** Atualiza apenas os dados do usuário */
  setUserData(userData: any): void {
    // Logs para debug (mantido da versão original)
    logger.debug('[TOKEN_SERVICE] Saving user data to storage', {
      hasData: !!userData?.data,
      permissions: userData?.data?.permissions,
      permissionsConfig: userData?.data?.permissionsConfig?.map((c: any) => ({
        perm_id: c.perm_id,
        role_name: c.role_name
      })),
      permissionsConfigLength: userData?.data?.permissionsConfig?.length || 0
    });
    
    TokenManager.setUserData(userData);
  },

  /** Atualiza apenas o accessToken */
  updateAccessToken(accessToken: string): void {
    TokenManager.updateAccessToken(accessToken);
  },

  /** Indica se há refreshToken disponível */
  hasRefreshToken(): boolean {
    return !!TokenManager.getRefreshToken();
  },

  /** Verifica se existe algum token válido */
  hasValidToken(): boolean {
    return TokenManager.hasValidToken();
  },

  /** Verifica se existe uma sessão ativa (access OR sso OR baseBearer) */
  hasActiveSession(): boolean {
    return TokenManager.hasActiveSession();
  },

  /** Limpa tokens (logout) */
  clearTokens(): void {
    TokenManager.clearTokens();
  },

  /** Limpa todos os dados de storage (debugging) */
  clearAllStorage(): void {
    logger.debug('[TOKEN_SERVICE] Clearing all storage for debugging');
    TokenManager.clearTokens();
    try {
      sessionStorage.clear();
      localStorage.clear();
    } catch (e) {
      logger.warn('[TOKEN_SERVICE] Falha ao limpar storage', { error: e instanceof Error ? e.message : String(e) });
    }
    logger.debug('[TOKEN_SERVICE] All storage cleared');
  }
};
